import { FEATURE_CREDIT_RULE } from '@/core/credit/types';

const DEFAULT_SUB_FEATURE_CODE = 'default';
const SUPPORTED_TIERS = Object.freeze(['free', 'standard', 'pro']);

const AVAILABILITY_STATE = Object.freeze({
    UNAVAILABLE: 'unavailable',
    PARTIAL: 'partial',
    FULL: 'full'
});

const LOCAL_FALLBACK_CAPABILITIES = Object.freeze([
    Object.freeze({
        capability_code: 'result_guide.hue_mapping_visual',
        source: 'fallback_local',
        tier_matrix: {
            free: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            standard: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            pro: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false }
        }
    }),
    Object.freeze({
        capability_code: 'result_guide.clipping_mapping_visual',
        source: 'fallback_local',
        tier_matrix: {
            free: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            standard: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            pro: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false }
        }
    }),
    Object.freeze({
        capability_code: 'result_guide.zone_boxplot_snapshot_visual',
        source: 'fallback_local',
        tier_matrix: {
            free: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            standard: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false },
            pro: { availability: 'full', credits: 1, visible_fields: '*', confirmation_required: true, default_in_run: false }
        }
    })
]);

function clone(value) {
    return JSON.parse(JSON.stringify(value));
}

function toTrimmedString(value) {
    return String(value || '').trim();
}

function normalizeAvailabilityState(value) {
    const raw = toTrimmedString(value).toLowerCase();
    if (raw === AVAILABILITY_STATE.PARTIAL) {
        return AVAILABILITY_STATE.PARTIAL;
    }
    if (raw === AVAILABILITY_STATE.FULL) {
        return AVAILABILITY_STATE.FULL;
    }
    return AVAILABILITY_STATE.UNAVAILABLE;
}

function normalizeCreditCost(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
        return 0;
    }
    return numeric;
}

function normalizeVisibleFields(value) {
    if (value === '*') {
        return '*';
    }
    if (!Array.isArray(value)) {
        return [];
    }

    const deduped = [];
    const seen = new Set();
    value.forEach((field) => {
        const key = toTrimmedString(field);
        if (!key || seen.has(key)) {
            return;
        }
        seen.add(key);
        deduped.push(key);
    });

    return deduped;
}

function resolveCreditRuleByTier({ confirmationRequired, credits, defaultInRun }) {
    if (confirmationRequired) {
        return FEATURE_CREDIT_RULE.CONFIRM;
    }
    if (credits > 0 && defaultInRun) {
        return FEATURE_CREDIT_RULE.DEFAULT;
    }
    return FEATURE_CREDIT_RULE.NONE;
}

function parseCapabilityCode(capabilityCode) {
    const raw = toTrimmedString(capabilityCode);
    if (!raw) {
        return null;
    }

    const dotIndex = raw.indexOf('.');
    if (dotIndex <= 0 || dotIndex === raw.length - 1) {
        return {
            featureCode: raw,
            subFeatureCode: DEFAULT_SUB_FEATURE_CODE
        };
    }

    return {
        featureCode: raw.slice(0, dotIndex),
        subFeatureCode: raw.slice(dotIndex + 1)
    };
}

function buildPolicyFeatureFromCapability(capability = {}) {
    const parsedCode = parseCapabilityCode(capability?.capability_code);
    if (!parsedCode) {
        return null;
    }

    const tierMatrix = capability?.tier_matrix && typeof capability.tier_matrix === 'object'
        ? capability.tier_matrix
        : {};

    const tierAvailability = {};
    const creditCostByTier = {};
    const tierAvailabilityStateByTier = {};
    const creditRuleByTier = {};
    const confirmRequiredByTier = {};
    const visibleFieldsByTier = {};
    const defaultInRunByTier = {};

    SUPPORTED_TIERS.forEach((tierId) => {
        const rawTierRule = tierMatrix[tierId]
            || {};

        const availabilityState = normalizeAvailabilityState(rawTierRule?.availability);
        const credits = availabilityState === AVAILABILITY_STATE.UNAVAILABLE
            ? 0
            : normalizeCreditCost(rawTierRule?.credits);
        const confirmationRequired = Boolean(rawTierRule?.confirmation_required);
        const defaultInRun = Boolean(rawTierRule?.default_in_run);

        tierAvailabilityStateByTier[tierId] = availabilityState;
        tierAvailability[tierId] = availabilityState !== AVAILABILITY_STATE.UNAVAILABLE;
        creditCostByTier[tierId] = credits;
        confirmRequiredByTier[tierId] = confirmationRequired;
        defaultInRunByTier[tierId] = defaultInRun;
        visibleFieldsByTier[tierId] = normalizeVisibleFields(rawTierRule?.visible_fields);
        creditRuleByTier[tierId] = resolveCreditRuleByTier({
            confirmationRequired,
            credits,
            defaultInRun
        });
    });

    return {
        featureCode: parsedCode.featureCode,
        subFeatureCode: parsedCode.subFeatureCode,
        tierAvailability,
        creditCostByTier,
        purchasable: SUPPORTED_TIERS.some((tierId) => tierAvailability[tierId]),
        tierAvailabilityStateByTier,
        creditRuleByTier,
        confirmRequiredByTier,
        visibleFieldsByTier,
        defaultInRunByTier,
        source: toTrimmedString(capability?.source) || 'backend'
    };
}

function normalizeCapabilityList(snapshot = {}) {
    if (!Array.isArray(snapshot?.capabilities)) {
        return [];
    }

    return snapshot.capabilities
        .filter((capability) => capability && typeof capability === 'object')
        .map((capability) => ({
            ...capability,
            capability_code: toTrimmedString(capability.capability_code)
        }))
        .filter((capability) => Boolean(capability.capability_code));
}

export function isCapabilityCatalogSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') {
        return false;
    }

    const hasPolicyVersion = Boolean(toTrimmedString(snapshot.policy_version));
    const hasCapabilities = Array.isArray(snapshot.capabilities);
    return hasPolicyVersion && hasCapabilities;
}

export function adaptCapabilityCatalogToFeaturePolicySnapshot(snapshot, options = {}) {
    if (!isCapabilityCatalogSnapshot(snapshot)) {
        throw new Error('[analysisPolicyAdapter] invalid capability catalog snapshot');
    }

    const {
        includeLocalFallback = true
    } = options;

    const normalizedCapabilities = normalizeCapabilityList(snapshot);
    const mergedCapabilities = includeLocalFallback
        ? [...normalizedCapabilities, ...LOCAL_FALLBACK_CAPABILITIES.map(clone)]
        : normalizedCapabilities;

    const featureMap = new Map();

    mergedCapabilities.forEach((capability) => {
        const featurePolicy = buildPolicyFeatureFromCapability(capability);
        if (!featurePolicy) {
            return;
        }

        const key = `${featurePolicy.featureCode}::${featurePolicy.subFeatureCode}`;
        const previous = featureMap.get(key);

        if (previous?.source === 'backend' && featurePolicy.source === 'fallback_local') {
            return;
        }

        featureMap.set(key, featurePolicy);
    });

    return {
        version: toTrimmedString(snapshot.policy_version) || 'capability-catalog',
        features: Array.from(featureMap.values())
    };
}

export {
    AVAILABILITY_STATE,
    LOCAL_FALLBACK_CAPABILITIES,
    SUPPORTED_TIERS
};
